import { useState, useEffect } from 'react';
import { FaShieldAlt, FaCheckCircle, FaTimesCircle, FaDatabase } from 'react-icons/fa';
import Table from '../components/common/Table.jsx';
import { CONST_SITE_LABEL, exportAccountScanToCSV, PLATFORM, STORAGE_KEYS } from '../components/common/Helper';
import SecurityScanApiService from '../services/SecurityScanApiService';
import DebugService from '../services/DebugService';
import SnackbarNotification, { SNACKBAR_THEME } from '../components/common/SnackbarNotification';
import { useAuth } from '../state/AuthContext';
import { useScopedLocalStorage } from '../hooks/useScopedLocalStorage';

/* ─── Severity badge colours ──────────────────────────────── */
const SEV_STYLE = {
  CRITICAL: { background: '#fde8e8', color: '#b91c1c' },
  HIGH:     { background: '#fde8e8', color: '#b91c1c' },
  MEDIUM:   { background: '#fff3e8', color: '#dd6f12' },
  LOW:      { background: '#eef4ff', color: '#2563eb' },
  INFO:     { background: '#f0fdf4', color: '#047857' },
};
const sevStyle = (s) => SEV_STYLE[(s || '').toUpperCase()] || { background: '#f1f5f9', color: '#475569' };

/* ─── Single-line truncating cell ─────────────────────────── */
const Cell = ({ value, w = 180, mono = false }) => {
  const safe = (value == null || typeof value === 'object') ? '—' : (String(value) || '—');
  return (
    <div
      title={safe}
      style={{
        width: w, maxWidth: w,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        fontFamily: mono ? 'ui-monospace,Menlo,monospace' : 'inherit',
        fontSize: mono ? 11.5 : 13,
        color: mono ? '#475569' : 'inherit',
        cursor: 'default',
      }}
    >
      {safe}
    </div>
  );
};

/* ─── Column definitions ──────────────────────────────────── */
const COLUMNS = [
  {
    key: 'Status', label: 'Status',
    render: (row) => {
      const passed = row.Status === 'PASSED';
      return (
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap',
          color: passed ? '#047857' : '#b91c1c',
        }}>
          <span style={{
            width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
            background: passed ? '#10b981' : '#ef4444', display: 'inline-block',
          }} />
          {row.Status}
        </span>
      );
    },
  },
  {
    key: 'Severity', label: 'Severity',
    render: (row) => (
      <span style={{
        fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 999,
        letterSpacing: '.03em', whiteSpace: 'nowrap', display: 'inline-block',
        ...sevStyle(row.Severity),
      }}>
        {row.Severity}
      </span>
    ),
  },
  { key: 'ServiceName',    label: 'Service Name',    render: (row) => <Cell value={row.ServiceName}    w={160} mono /> },
  { key: 'Region',         label: 'Region',          render: (row) => <Cell value={row.Region}         w={130} /> },
  { key: 'CheckID',        label: 'Check ID',        render: (row) => <Cell value={row.CheckID}        w={220} mono /> },
  { key: 'CheckTitle',     label: 'Check Title',     render: (row) => <Cell value={row.CheckTitle}     w={220} /> },
  { key: 'ResourceId',     label: 'Resource ID',     render: (row) => <Cell value={row.ResourceId}     w={260} mono /> },
  {
    key: 'ResourceTags', label: 'Resource Tags',
    render: (row) => {
      const tags = row.ResourceTags;
      if (!tags || Object.keys(tags).length === 0) return <Cell value="N/A" w={140} />;
      const summary = Object.entries(tags).map(([k, v]) => `${k}: ${v}`).join(' | ');
      return <Cell value={summary} w={180} />;
    },
  },
  { key: 'StatusExtended', label: 'Status Extended', render: (row) => <Cell value={row.StatusExtended} w={220} /> },
  { key: 'Risk',           label: 'Risk',            render: (row) => <Cell value={row.Risk}           w={120} /> },
  { key: 'Recommendation', label: 'Recommendation',  render: (row) => <Cell value={row.Recommendation} w={220} /> },
  { key: 'Compliance',     label: 'Compliance',      render: (row) => <Cell value={row.Compliance}     w={220} /> },
];

/* ─── Stat card data ──────────────────────────────────────── */
const STAT_CARDS = (info) => [
  { label: 'Total Findings',  value: info.totalFindings,  bg: '#eef4ff', ibg: '#dbe8ff', icolor: '#3b82f6', numColor: '#1d4ed8', icon: <FaShieldAlt /> },
  { label: 'Passed',          value: info.passed,         bg: '#e9f9f1', ibg: '#cdf2e1', icolor: '#10b981', numColor: '#047857', icon: <FaCheckCircle /> },
  { label: 'Failed',          value: info.failed,         bg: '#fdecec', ibg: '#fbd5d5', icolor: '#ef4444', numColor: '#b91c1c', icon: <FaTimesCircle /> },
  { label: 'Total Resources', value: info.totalResources, bg: '#f4ecfd', ibg: '#ecd9fb', icolor: '#a855f7', numColor: '#7e22ce', icon: <FaDatabase /> },
];

export default function AccountScan() {
  const [isLoading, setIsLoading]             = useState(false);
  const [isReportLoading, setIsReportLoading] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState(null);
  const [snackbarStatus, setSnackbarStatus]   = useState(null);
  const [scanReportList, setScanReportList]   = useState([]);
  const [scanReport, setScanReport]           = useState([]);
  const [selectedItem, setSelectedItem]       = useState('');
  const [isScanAvailable, setIsScanAvailable] = useState(false);
  const [reportInfo, setReportInfo]           = useState({ totalFindings: 0, passed: 0, failed: 0, totalResources: 0 });
  const [filterChip, setFilterChip]           = useState('All');
  const [searchQuery, setSearchQuery]         = useState('');

  const { selectedAccount, selectedPlatform } = useAuth();
  const { getScopedItem } = useScopedLocalStorage();

  /* ─── Build rows from raw data ────────────────────────── */
  const safeStr = (v) => (v == null || typeof v === 'object') ? null : String(v);
  const safeJoin = (v) => Array.isArray(v) ? v.join(', ') : null;

  const allRows = (scanReport?._source?.parsedJson ?? []).map((item, i) => ({
    id:             `${scanReport['_id']}-${i}`,
    Status:         safeStr(item.Compliance?.Status)                           ?? 'N/A',
    Severity:       safeStr(item.Severity?.Label)                              ?? 'N/A',
    ServiceName:    safeStr(item.GeneratorId?.replace(/^prowler-/, ''))        ?? 'N/A',
    Region:         safeStr(item.Resources?.[0]?.Region)                       ?? 'N/A',
    CheckID:        safeStr(item.Id?.replace(/^prowler-/, ''))                 ?? 'N/A',
    CheckTitle:     safeStr(item.Title)                                        ?? 'N/A',
    ResourceId:     safeStr(item.Resources?.[0]?.Id)                           ?? 'N/A',
    ResourceTags:   item.Resources?.[0]?.Tags                                  ?? null,
    StatusExtended: safeStr(item.Description)                                  ?? 'N/A',
    Risk:           safeJoin(item.Types)                                       ?? 'N/A',
    Recommendation: safeStr(item.Remediation?.Recommendation?.Text)            ?? 'N/A',
    Compliance:     safeJoin(item.Compliance?.RelatedRequirements)             ?? 'N/A',
  }));

  /* ─── Chip + search filter ────────────────────────────── */
  const filteredRows = allRows.filter((row) => {
    const matchChip =
      filterChip === 'All'    ? true :
      filterChip === 'Failed' ? row.Status === 'FAILED' :
      filterChip === 'Passed' ? row.Status === 'PASSED' :
      filterChip === 'Low'    ? row.Severity?.toUpperCase() === 'LOW' : true;

    const q = searchQuery.toLowerCase();
    const matchSearch = !q || [
      row.CheckTitle, row.ServiceName, row.Region, row.CheckID,
      row.StatusExtended, row.Risk, row.Recommendation, row.Compliance, row.ResourceId,
    ].some(v => (v || '').toLowerCase().includes(q));

    return matchChip && matchSearch;
  });

  const passRate = reportInfo.totalFindings > 0
    ? ((reportInfo.passed / reportInfo.totalFindings) * 100).toFixed(1)
    : 0;

  /* ─── API handlers ────────────────────────────────────── */
  const accountScanHandler = () => {
    setIsLoading(true);
    setSnackbarMessage(null);
    setSnackbarStatus(null);

    const userDetails = getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS);
    const parseToJson = JSON.parse(userDetails);
    const dataRequest = {};
    if (selectedAccount)       dataRequest['userAccountId'] = selectedAccount;
    if (parseToJson['userId']) dataRequest['userId']        = parseToJson['userId'];

    DebugService.log('Account Scan Component: Request to scan initiated', dataRequest);

    const scanFn = selectedPlatform === PLATFORM.AWS
      ? SecurityScanApiService.scanAccount
      : SecurityScanApiService.scanAzureAccount;

    scanFn(dataRequest)
      .then(data => {
        if (data['data']) { setIsScanAvailable(false); setSnackbarStatus('success'); setSnackbarMessage('Scan initiated successfully. This will take 10-15 minutes to complete.'); }
        if (data['error']) { setSnackbarStatus('failed'); setSnackbarMessage(data['error']); }
        setIsLoading(false);
      })
      .catch(() => { setSnackbarStatus('failed'); setSnackbarMessage('Something went wrong. Please try later'); setIsLoading(false); });
  };

  const fetchDataList = () => {
    setScanReportList([]); setSelectedItem(''); setIsScanAvailable(false);
    const listFn = selectedPlatform === PLATFORM.AWS
      ? SecurityScanApiService.getScanReportList
      : SecurityScanApiService.getAzureScanReportList;

    listFn(selectedAccount)
      .then(data => {
        if (data['data']) {
          setScanReportList(data['data'] ?? []);
          if (data['data'].length > 0) setSelectedItem(data['data'][0].openSearchId);
        }
        if (data['error']) { setSnackbarStatus('failed'); setSnackbarMessage(data['error']); }
        if (data['isScanAvailable']) setIsScanAvailable(data['isScanAvailable']);
        setIsLoading(false);
      })
      .catch(() => { setSnackbarStatus('failed'); setSnackbarMessage('Something went wrong while fetching Report List'); setIsLoading(false); });
  };

  const fetchData = (openSearchId) => {
    setScanReport([]); setReportInfo({ totalFindings: 0, passed: 0, failed: 0, totalResources: 0 });
    const reportFn = selectedPlatform === PLATFORM.AWS
      ? SecurityScanApiService.getScanReport
      : SecurityScanApiService.getAzureScanReport;

    reportFn({ selectedAccount, openSearchId })
      .then(data => {
        if (data['data']) setScanReport(data['data'] ?? []);
        const findings = data['data']?._source?.parsedJson || [];
        let passed = 0, failed = 0;
        const resourceSet = new Set();
        findings.forEach((f) => {
          const st = f?.Compliance?.Status;
          if (st === 'PASSED') passed++;
          if (st === 'FAILED') failed++;
          if (Array.isArray(f?.Resources)) f.Resources.forEach((r) => { if (r?.Id) resourceSet.add(r.Id); });
        });
        setReportInfo({ totalFindings: findings.length, passed, failed, totalResources: resourceSet.size });
        if (data['error']) { setSnackbarStatus('failed'); setSnackbarMessage(data['error']); }
        setIsReportLoading(false);
      })
      .catch(() => { setSnackbarStatus('failed'); setSnackbarMessage('Something went wrong while fetching Report'); setIsReportLoading(false); });
  };

  useEffect(() => { if (!selectedAccount || !selectedItem) return; setIsReportLoading(true); fetchData(selectedItem); }, [selectedAccount, selectedItem]);
  useEffect(() => { if (!selectedAccount) return; setSnackbarStatus(null); setSnackbarMessage(null); setIsLoading(false); fetchDataList(); }, [selectedAccount]);
  useEffect(() => { document.title = `${CONST_SITE_LABEL} | Security Scan`; }, []);

  /* ─── Render ──────────────────────────────────────────── */
  return (
    <div style={{ padding: '16px 28px 48px', background: '#f4f6fa', minHeight: '100vh' }}>

      {/* ── Scan Controls ─────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <button
          onClick={accountScanHandler}
          disabled={!isScanAvailable || isLoading}
          title={!isScanAvailable ? 'Scan will be available after sometime' : undefined}
          style={{
            background: '#f5821f', color: '#fff', border: 'none', borderRadius: 10,
            padding: '9px 18px', fontSize: 13, fontWeight: 700,
            cursor: (!isScanAvailable || isLoading) ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', gap: 8,
            boxShadow: '0 4px 12px rgba(245,130,31,.2)', transition: 'background .15s',
            opacity: (!isScanAvailable || isLoading) ? 0.65 : 1,
          }}
        >
          {isLoading ? 'Scanning…' : 'Click to Scan'} <span style={{ fontSize: 15 }}>⛶</span>
        </button>

        <div style={{ position: 'relative', border: '1px solid #e6e9f0', borderRadius: 10, padding: '5px 36px 5px 12px', minWidth: 320, background: '#fff' }}>
          <span style={{ fontSize: 9.5, color: '#6b7280', position: 'absolute', top: -7, left: 10, background: '#fff', padding: '0 4px' }}>Select Report</span>
          <select
            value={selectedItem}
            onChange={(e) => setSelectedItem(e.target.value)}
            style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 13, fontWeight: 600, color: '#1f2937', fontFamily: 'ui-monospace,Menlo,monospace', width: '100%', cursor: 'pointer' }}
          >
            {scanReportList.length === 0 && <option value="">No Report Available</option>}
            {scanReportList.map((d) => <option key={d.openSearchId} value={d.openSearchId}>{d.openSearchId}</option>)}
          </select>
        </div>

        {scanReportList.length > 0 && (
          <button
            title="Download report"
            onClick={() => exportAccountScanToCSV(scanReport?._source?.parsedJson, selectedItem)}
            style={{ background: '#4d5a73', color: '#fff', border: 'none', borderRadius: 10, width: 34, height: 34, fontSize: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >⤓</button>
        )}
      </div>

      {!isScanAvailable && (
        <p style={{ fontSize: 11, color: '#9ca3af', textAlign: 'center', marginTop: -12, marginBottom: 16 }}>Scan will be available after sometime</p>
      )}

      {/* ── Stat Cards ────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 16 }}>
        {STAT_CARDS(reportInfo).map((card) => (
          <div key={card.label} style={{ borderRadius: 12, padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 12, background: card.bg, boxShadow: '0 1px 2px rgba(16,24,40,.04),0 4px 14px rgba(16,24,40,.05)', border: '1px solid rgba(0,0,0,.02)' }}>
            <div style={{ width: 42, height: 42, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, background: card.ibg, color: card.icolor, flexShrink: 0 }}>{card.icon}</div>
            <div>
              <div style={{ fontSize: 24, fontWeight: 750, letterSpacing: '-.02em', color: card.numColor, marginTop: 1 }}>
                {isReportLoading ? '—' : card.value.toLocaleString()}
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>{card.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Compliance Health Bar ─────────────────────────── */}
      <div style={{ background: '#fff', border: '1px solid #e6e9f0', borderRadius: 12, boxShadow: '0 1px 2px rgba(16,24,40,.04),0 4px 14px rgba(16,24,40,.05)', padding: '10px 18px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' }}>
          Compliance health <span style={{ color: '#6b7280', fontWeight: 500 }}>· {reportInfo.passed.toLocaleString()} passed of {reportInfo.totalFindings.toLocaleString()} checks</span>
        </div>
        <div style={{ flex: 1, minWidth: 240, height: 8, borderRadius: 999, background: '#f1f5f9', overflow: 'hidden', display: 'flex' }}>
          <div style={{ width: `${passRate}%`, background: 'linear-gradient(90deg,#10b981,#34d399)', height: '100%' }} />
          <div style={{ width: `${100 - passRate}%`, background: 'linear-gradient(90deg,#f87171,#ef4444)', height: '100%' }} />
        </div>
        <div style={{ fontSize: 18, fontWeight: 750, color: '#047857', whiteSpace: 'nowrap' }}>{passRate}%</div>
        <div style={{ display: 'flex', gap: 12, fontSize: 11, color: '#6b7280' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 8, height: 8, borderRadius: 2.5, background: '#10b981', display: 'inline-block' }} />Passed {reportInfo.passed.toLocaleString()}</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 8, height: 8, borderRadius: 2.5, background: '#ef4444', display: 'inline-block' }} />Failed {reportInfo.failed.toLocaleString()}</span>
        </div>
      </div>

      {/* ── Findings Table ────────────────────────────────── */}
      <Table
        rows={filteredRows}
        columns={COLUMNS}
        loading={isReportLoading}
        defaultPageSize={100}
        pageSizes={[25, 50, 100, 200]}
        onExport={() => exportAccountScanToCSV(scanReport?._source?.parsedJson, selectedItem)}
        emptyText="No findings match the current filter."
        headerExtra={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
            <span style={{ fontSize: 15, fontWeight: 700 }}>Scan Findings</span>
            <div style={{ display: 'flex', gap: 8 }}>
              {['All', 'Failed', 'Passed', 'Low'].map((chip) => (
                <button
                  key={chip}
                  onClick={() => setFilterChip(chip)}
                  style={{
                    fontSize: 12.5, padding: '5px 14px', borderRadius: 999, fontWeight: 600, cursor: 'pointer',
                    border: `1px solid ${filterChip === chip ? '#ffd9b5' : '#e6e9f0'}`,
                    background: filterChip === chip ? '#fff3e8' : '#fff',
                    color: filterChip === chip ? '#dd6f12' : '#6b7280',
                    transition: 'all .12s',
                  }}
                >
                  {chip}
                </button>
              ))}
            </div>
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="⌕  Search check, service, region…"
              style={{ background: '#f8fafc', border: '1px solid #e6e9f0', borderRadius: 10, padding: '7px 14px', fontSize: 13, color: '#6b7280', minWidth: 220, outline: 'none' }}
            />
          </div>
        }
        subscriptionGated
      />

      {/* Snackbar */}
      {snackbarStatus && (
        <SnackbarNotification
          initialOpen={true} duration={5000} message={snackbarMessage}
          actionButtonName="Ok"
          theme={snackbarStatus === 'success' ? SNACKBAR_THEME.GREEN : SNACKBAR_THEME.RED}
          yPosition="top" xPosition="center"
        />
      )}
    </div>
  );
}
